import {
  html,
  attr,
  prop,
  Signal,
  ForEach,
  Fragment,
  OneOfValue,
  computedOf,
  When,
  Async,
} from '@tempots/dom'
import { Anchor } from '@tempots/ui'
import { Icon, TextInput } from '@tempots/beatui'
import { PageLayout } from '@/components/layout'
import { LoadingState } from '@/components/ui/loading-state'
import { ErrorState } from '@/components/ui/error-state'
import { TagFilter } from '@/components/ui/tag-filter'
import { actions as actionsService } from '@/services/gateway'
import type { ActionSummary } from '@/services/types'
import { Workspace } from '@/state/workspace'

// Simple action type for display
interface ActionDisplay {
  name: string
  description: string
  creditCost: number
  tags: string[]
}

export interface ActionsPageOptions {
  projectId: Signal<string>
}

export function ActionsPage({ projectId }: ActionsPageOptions) {
  projectId.on(Workspace.changeProject)
  const allActions = prop<ActionDisplay[]>([])
  const searchQuery = prop('')
  const selectedTags = prop<string[]>([])

  // Filtered actions based on search and tag filter
  const filteredActions = computedOf(
    allActions,
    searchQuery,
    selectedTags
  )((actions, search, tags) => {
    // Filter by search query
    let filtered = actions
    if (search.trim()) {
      const query = search.toLowerCase()
      filtered = filtered.filter(
        (action) =>
          action.name.toLowerCase().includes(query) ||
          action.description.toLowerCase().includes(query)
      )
    }

    // Filter by tags (OR logic - action matches if it has any selected tag)
    if (tags.length > 0) {
      filtered = filtered.filter((action) =>
        action.tags.some((tag) => tags.includes(tag))
      )
    }

    return filtered
  })

  // Extract all unique tags from actions for the filter
  const availableTags = allActions.map((actions) => {
    const tagSet = new Set<string>()
    actions.forEach((a) => a.tags.forEach((t) => tagSet.add(t)))
    return Array.from(tagSet).sort()
  })

  // View state for content area (mutually exclusive states)
  const viewState = computedOf(
    allActions,
    filteredActions
  )((all, filtered): 'empty' | 'no-matches' | 'content' => {
    if (all.length === 0) return 'empty'
    if (filtered.length === 0) return 'no-matches'
    return 'content'
  })

  // Load actions from API - returns promise for Async component
  const loadActions = async () => {
    const response = await actionsService.list()
    // Map API response to display format
    // eslint-disable-next-line tempots/require-async-signal-disposal -- plain array, not signal
    const displayActions: ActionDisplay[] = response.actions.map(
      (action: ActionSummary) => ({
        name: action.name,
        description: action.description,
        creditCost: action.creditFormula?.baseCredits ?? 0,
        tags: action.tags ?? [],
      })
    )
    allActions.set(displayActions)
    return displayActions
  }

  // Create the promise for async loading
  const actionsPromise = loadActions()

  // Debounced search handler
  const handleSearchChange = (value: string) => {
    searchQuery.set(value)
  }

  return PageLayout({
    header: 'Actions',
    content: Async(actionsPromise, {
      pending: () =>
        LoadingState({ message: 'Loading actions...', size: 'lg' }),
      error: (error) =>
        Fragment(
          // Description
          html.p(
            attr.class('text-sm text-gray-600'),
            'Available API actions for processing your files. Credits are deducted per operation.'
          ),
          // Error message
          ErrorState({
            error: prop<string | null>(
              error instanceof Error ? error.message : 'Failed to load actions'
            ),
          })
        ),
      then: () =>
        Fragment(
          // Description
          html.p(
            attr.class('text-sm text-gray-600'),
            'Available API actions for processing your files. Credits are deducted per operation.'
          ),

          // Search and Filter Controls
          html.div(
            attr.class('flex items-center gap-4 mt-4 mb-6'),
            // Search input
            html.div(
              attr.class('flex-1'),
              TextInput({
                value: searchQuery,
                placeholder: 'Search actions...',
                before: Icon(
                  { icon: 'mdi:magnify' },
                  attr.class('w-5 h-5 text-gray-400 pl-2')
                ),
                onChange: handleSearchChange,
              })
            ),
            // Tag filter
            TagFilter({
              availableTags,
              selectedTags,
              placeholder: 'Filter by tags...',
              onChange: (tags) => selectedTags.set(tags),
            })
          ),

          // Content area - mutually exclusive states
          OneOfValue(viewState, {
            empty: () =>
              html.div(
                attr.class('text-center py-12'),
                Icon(
                  { icon: 'mdi:lightning-bolt-outline' },
                  attr.class('w-16 h-16 text-gray-300 mx-auto mb-4')
                ),
                html.p(
                  attr.class('text-gray-600'),
                  'No actions available. Check back later.'
                )
              ),
            'no-matches': () =>
              html.div(
                attr.class('text-center py-12'),
                Icon(
                  { icon: 'mdi:magnify' },
                  attr.class('w-16 h-16 text-gray-300 mx-auto mb-4')
                ),
                html.p(attr.class('text-gray-600 mb-2'), 'No actions found'),
                html.p(
                  attr.class('text-sm text-gray-500'),
                  'Try adjusting your search or filter criteria.'
                )
              ),
            content: () =>
              html.div(
                attr.class(
                  'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6'
                ),
                ForEach(filteredActions, (action) => ActionCard(action))
              ),
          })
        ),
    }),
  })
}

function formatTagName(tag: string): string {
  // Convert "image" to "Image", "conversion" to "Conversion", etc.
  return tag.charAt(0).toUpperCase() + tag.slice(1)
}

// Color schemes for different tag categories
const tagColorSchemes: Record<string, string> = {
  image: 'bg-blue-100 text-blue-700',
  ai: 'bg-purple-100 text-purple-700',
  resize: 'bg-green-100 text-green-700',
  conversion: 'bg-amber-100 text-amber-700',
  thumbnail: 'bg-teal-100 text-teal-700',
  optimization: 'bg-emerald-100 text-emerald-700',
  crop: 'bg-orange-100 text-orange-700',
  upscaling: 'bg-indigo-100 text-indigo-700',
  analysis: 'bg-cyan-100 text-cyan-700',
  metadata: 'bg-slate-100 text-slate-700',
  data: 'bg-rose-100 text-rose-700',
  generation: 'bg-fuchsia-100 text-fuchsia-700',
  testing: 'bg-lime-100 text-lime-700',
  text: 'bg-sky-100 text-sky-700',
  transformation: 'bg-violet-100 text-violet-700',
}

function getTagBadgeClasses(tag: string): string {
  const colorScheme =
    tagColorSchemes[tag.toLowerCase()] ?? 'bg-gray-100 text-gray-600'
  return `inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${colorScheme}`
}

function ActionCard(action: Signal<ActionDisplay>) {
  // Combine action and projectId signals for reactive URL
  const href = computedOf(
    action,
    Workspace.projectId
  )((a, pid) => Workspace.projectUrl(`/actions/${a.name}`, pid) ?? '#')

  return Anchor(
    { href },
    attr.class(
      'block bg-white border border-gray-200 rounded-xl p-6 hover:border-brand-300 hover:shadow-sm transition-all'
    ),
    html.div(
      attr.class('flex items-start justify-between mb-4'),
      html.div(
        attr.class(
          'w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center'
        ),
        Icon(
          { icon: 'mdi:lightning-bolt' },
          attr.class('w-6 h-6 text-brand-600')
        )
      ),
      html.span(
        attr.class(
          'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700'
        ),
        action.map((a) => a.creditCost),
        ' credits'
      )
    ),
    html.h3(
      attr.class('text-lg font-semibold text-gray-900 mb-2'),
      action.map((a) => a.name)
    ),
    html.p(
      attr.class('text-sm text-gray-600 mb-3'),
      action.map((a) => a.description)
    ),
    // Tags display as colored badges
    When(
      action.map((a) => a.tags.length > 0),
      () =>
        html.div(
          attr.class('flex flex-wrap gap-1.5 mb-3'),
          ForEach(
            action.map((a) => a.tags),
            (tag) =>
              html.span(
                attr.class(tag.map((t) => getTagBadgeClasses(t))),
                tag.map((t) => formatTagName(t))
              )
          )
        )
    ),
    html.div(
      attr.class('flex items-center text-sm text-brand-600 font-medium'),
      'View Details',
      Icon({ icon: 'mdi:chevron-right' }, attr.class('w-4 h-4 ml-1'))
    )
  )
}
